import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { User, Session } from "@/types";
import { User as SupabaseUser, Session as SupabaseSession } from "@supabase/supabase-js";

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // SECURITY: Sanitized logging - only log events in dev mode, never log PII
      if (import.meta.env.DEV) {
        console.log("[Auth] State change:", event, session ? "session active" : "no session");
      }
      setSession(session as Session | null);
      setUser(session?.user as User | null);
      setLoading(false);
    });

    // If returning from OAuth (PKCE), exchange code for a session explicitly
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const state = url.searchParams.get("state");
    if (code && state) {
      if (import.meta.env.DEV) {
        console.log("[Auth] OAuth callback detected, exchanging code for session");
      }
      supabase.auth.exchangeCodeForSession(code).then(({ data, error }) => {
        if (error) {
          // SECURITY: Sanitized error logging - no sensitive details
          if (import.meta.env.DEV) {
            console.error("[Auth] Code exchange failed:", error.message);
          }
        } else {
          if (import.meta.env.DEV) {
            console.log("[Auth] Code exchange successful");
          }
        }
        // Clean the URL params after exchange
        try {
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch {
          // Ignore history API errors in environments where it's restricted
        }
      });
    }

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (import.meta.env.DEV) {
        console.log("[Auth] Initial session check:", session ? "authenticated" : "unauthenticated");
      }
      setSession(session as Session | null);
      setUser(session?.user as User | null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });
    return { data, error };
  };

  const signInWithBitbucket = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "bitbucket",
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });
    return { data, error };
  };

  return {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    signInWithBitbucket,
  };
}
